import { useState, useEffect } from "react";
import { Download, Eye, Ticket } from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { toast } from "sonner";

import { API } from "../../config/api";

// Helper to format ticket info
const formatTicketSummary = (tickets) => {
  if (!tickets || tickets.length === 0) return "No tickets";
  
  return tickets.map(t => {
    const slotName = t.slot_name || t.slot_id || "";
    const categoryName = t.category_name || (t.category_id === "A" ? "Adult" : t.category_id === "B" ? "Kids" : "Infant");
    const qty = t.quantity || 1;
    return `${slotName} - ${categoryName} x${qty}`;
  }).join(", ");
};

// Get unique slots from tickets
const getSlotsSummary = (tickets) => {
  if (!tickets || tickets.length === 0) return "-";
  const slots = [...new Set(tickets.map(t => t.slot_name || t.slot_id || "").filter(Boolean))];
  return slots.join(", ") || "-";
};

// Get ticket categories summary
const getCategoriesSummary = (tickets) => {
  if (!tickets || tickets.length === 0) return "-";
  const summary = {};
  tickets.forEach(t => {
    const cat = t.category_name || (t.category_id === "A" ? "Adult" : t.category_id === "B" ? "Kids" : "Infant");
    const qty = t.quantity || 1;
    summary[cat] = (summary[cat] || 0) + qty;
  });
  return Object.entries(summary).map(([cat, qty]) => `${cat}: ${qty}`).join(", ");
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await fetch(`${API}/admin/orders`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
      });
      if (response.ok) {
        setOrders(await response.json());
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${API}/admin/orders/export`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
      });

      if (response.ok) {
        const data = await response.json();
        const blob = new Blob([data.csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `orders-${new Date().toISOString().split("T")[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success(`Exported ${data.count} orders`);
      } else {
        toast.error("Failed to export orders");
      }
    } catch (error) {
      toast.error("Error exporting orders");
    } finally {
      setExporting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid": return "bg-green-100 text-green-700";
      case "pending": return "bg-yellow-100 text-yellow-700";
      case "refunded": return "bg-red-100 text-red-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  const stats = {
    total: orders.length,
    paid: orders.filter(o => o.status === "paid").length,
    pending: orders.filter(o => o.status === "pending").length,
    revenue: orders.filter(o => o.status === "paid").reduce((sum, o) => sum + (o.amount_cents || 0), 0)
  };

  return (
    <div className="space-y-6" data-testid="admin-orders">
      <div className="flex justify-between items-center">
        <h1 className="font-heading text-2xl text-kerala-green">Orders</h1>
        <Button
          variant="outline"
          onClick={handleExport}
          disabled={exporting || orders.length === 0}
          data-testid="export-orders-btn"
        >
          {exporting ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin w-4 h-4 border-2 border-kerala-green border-t-transparent rounded-full" />
              Exporting...
            </span>
          ) : (
            <>
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </>
          )}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-sm border border-kerala-gold/10">
          <p className="text-sm text-gray-500">Total Orders</p>
          <p className="text-2xl font-semibold text-kerala-green">{stats.total}</p>
        </div>
        <div className="bg-white p-4 rounded-sm border border-kerala-gold/10">
          <p className="text-sm text-gray-500">Paid</p>
          <p className="text-2xl font-semibold text-green-600">{stats.paid}</p>
        </div>
        <div className="bg-white p-4 rounded-sm border border-kerala-gold/10">
          <p className="text-sm text-gray-500">Pending</p>
          <p className="text-2xl font-semibold text-yellow-600">{stats.pending}</p>
        </div>
        <div className="bg-white p-4 rounded-sm border border-kerala-gold/10">
          <p className="text-sm text-gray-500">Revenue</p>
          <p className="text-2xl font-semibold text-kerala-green">${(stats.revenue / 100).toFixed(2)}</p>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-sm border border-kerala-gold/10">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-kerala-green border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : orders.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No orders yet.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order ID</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Slot</TableHead>
                <TableHead>Tickets</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.id}>
                  <TableCell className="font-mono text-sm">
                    {order.id.slice(0, 8)}...
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{order.customer_name}</p>
                      <p className="text-sm text-gray-500">{order.customer_email}</p>
                      {order.customer_phone && (
                        <p className="text-xs text-gray-400">{order.customer_phone}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="inline-flex items-center px-2 py-1 rounded-md bg-kerala-green/10 text-kerala-green text-xs font-medium">
                      {getSlotsSummary(order.tickets)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {getCategoriesSummary(order.tickets)}
                    </div>
                  </TableCell>
                  <TableCell className="font-semibold">${((order.amount_cents || 0) / 100).toFixed(2)}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs capitalize ${getStatusColor(order.status)}`}>
                      {order.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">
                    {new Date(order.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setSelectedOrder(order)}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Order Detail Dialog */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Ticket className="w-5 h-5 text-kerala-green" />
              Order Details
            </DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Order ID</p>
                  <p className="font-mono text-xs">{selectedOrder.id}</p>
                </div>
                <div>
                  <p className="text-gray-500">Status</p>
                  <span className={`px-2 py-1 rounded-full text-xs capitalize ${getStatusColor(selectedOrder.status)}`}>
                    {selectedOrder.status}
                  </span>
                </div>
                <div>
                  <p className="text-gray-500">Customer Name</p>
                  <p className="font-medium">{selectedOrder.customer_name}</p>
                </div>
                <div>
                  <p className="text-gray-500">Customer Email</p>
                  <p className="text-sm">{selectedOrder.customer_email}</p>
                </div>
                {selectedOrder.customer_phone && (
                  <div>
                    <p className="text-gray-500">Phone</p>
                    <p>{selectedOrder.customer_phone}</p>
                  </div>
                )}
                <div>
                  <p className="text-gray-500">Total Amount</p>
                  <p className="text-xl font-semibold text-kerala-green">
                    ${((selectedOrder.amount_cents || 0) / 100).toFixed(2)} CAD
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Order Date</p>
                  <p>{new Date(selectedOrder.created_at).toLocaleString()}</p>
                </div>
              </div>

              {/* Tickets Section with Slot & Category Details */}
              {selectedOrder.tickets && selectedOrder.tickets.length > 0 && (
                <div className="border-t pt-4">
                  <p className="text-gray-500 mb-3 font-medium flex items-center gap-2">
                    <Ticket className="w-4 h-4" />
                    Ticket Details ({selectedOrder.tickets.reduce((sum, t) => sum + (t.quantity || 1), 0)} tickets)
                  </p>
                  <div className="bg-gray-50 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-kerala-green/10">
                        <tr>
                          <th className="px-3 py-2 text-left text-kerala-green font-medium">Slot</th>
                          <th className="px-3 py-2 text-left text-kerala-green font-medium">Category</th>
                          <th className="px-3 py-2 text-center text-kerala-green font-medium">Qty</th>
                          <th className="px-3 py-2 text-right text-kerala-green font-medium">Price</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {selectedOrder.tickets.map((ticket, idx) => {
                          const slotDisplay = ticket.slot_name || ticket.slot_id || "N/A";
                          const slotTime = ticket.slot_time || "";
                          const categoryDisplay = ticket.category_name || 
                            (ticket.category_id === "A" ? "Adult" : 
                             ticket.category_id === "B" ? "Kids" : 
                             ticket.category_id === "C" ? "Infant" : ticket.category_id);
                          const qty = ticket.quantity || 1;
                          const pricePerUnit = ticket.price_cents || 0;
                          const totalPrice = pricePerUnit * qty;
                          
                          return (
                            <tr key={idx} className="hover:bg-gray-100">
                              <td className="px-3 py-2">
                                <div>
                                  <span className="font-medium text-kerala-green">{slotDisplay}</span>
                                  {slotTime && (
                                    <p className="text-xs text-gray-400">{slotTime}</p>
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-2">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  categoryDisplay === "Adult" ? "bg-blue-100 text-blue-700" :
                                  categoryDisplay === "Kids" ? "bg-purple-100 text-purple-700" :
                                  "bg-green-100 text-green-700"
                                }`}>
                                  {categoryDisplay}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-center font-medium">{qty}</td>
                              <td className="px-3 py-2 text-right">
                                {totalPrice > 0 ? `$${(totalPrice / 100).toFixed(2)}` : "FREE"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-kerala-green/5">
                        <tr>
                          <td colSpan="3" className="px-3 py-2 text-right font-medium">Total:</td>
                          <td className="px-3 py-2 text-right font-bold text-kerala-green">
                            ${((selectedOrder.amount_cents || 0) / 100).toFixed(2)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {selectedOrder.stripe_session_id && (
                <div className="border-t pt-4">
                  <p className="text-gray-500 text-xs">Stripe Session ID</p>
                  <p className="font-mono text-xs break-all text-gray-400">{selectedOrder.stripe_session_id}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
